import React, { useEffect, useState } from "react";
import { useRegTheme } from "../theme";
import { ToastifyAlert } from "../../../CommonComponents/ToastifyAlert";
import {
  loadPrinterRole,
  savePrinterRole,
  hasTarget,
  listPrinters,
  openDrawerOn,
  testPrintOn,
  getKnownNetworkPrinters,
  rememberNetworkPrinter,
  getPaperWidthMm,
  setPaperWidthMm,
} from "../data/printers";

// Printer Settings (browser equivalent of Android's Printers screen). Pick a Payment Receipt printer
// and a Packing Slip printer, per lane (saved locally). The cash drawer opens via the Receipt printer.
// Installed printers are auto-detected via QZ; a network printer not installed in Windows (e.g. a Star
// TSP100 LAN) is entered by IP.
export default function PrinterSettingsModal({ onClose }) {
  const t = useRegTheme();
  const s = styles(t);

  const [receipt, setReceipt] = useState(() => loadPrinterRole("receipt"));
  const [packing, setPacking] = useState(() => loadPrinterRole("packing"));
  const [detected, setDetected] = useState([]);
  const [known, setKnown] = useState(() => getKnownNetworkPrinters()); // remembered network IPs
  const [paperWidth, setPaperWidth] = useState(() => getPaperWidthMm());
  const [detecting, setDetecting] = useState(false);
  const [detectMsg, setDetectMsg] = useState("");
  const [busy, setBusy] = useState("");

  const detect = async () => {
    setDetecting(true);
    setDetectMsg("");
    const r = await listPrinters();
    setDetecting(false);
    if (r.ok) {
      setDetected(r.printers);
      setDetectMsg(r.printers.length ? `Found ${r.printers.length} printer(s)` : "No installed printers — use Network (IP)");
    } else {
      setDetectMsg(r.reason === "no-bridge" ? "QZ Tray not reachable — is it running?" : "Detect failed: " + (r.error || r.reason));
    }
  };

  useEffect(() => {
    detect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runTest = async (label, fn, cfg) => {
    if (!hasTarget(cfg)) return ToastifyAlert("Pick a printer or enter an IP first", "error");
    setBusy(label);
    const r = await fn(cfg);
    setBusy("");
    if (r.ok) {
      ToastifyAlert(label + " sent", "success");
      if (cfg.mode === "network" && cfg.host) {
        rememberNetworkPrinter(cfg.host, cfg.port); // a working IP → keep it in the picker
        setKnown(getKnownNetworkPrinters());
      }
    } else {
      ToastifyAlert(label + " failed: " + (r.reason === "no-bridge" ? "QZ Tray not running" : r.error || r.reason), "error");
    }
  };

  const save = () => {
    savePrinterRole("receipt", receipt);
    savePrinterRole("packing", packing);
    setPaperWidthMm(paperWidth);
    ToastifyAlert("Printer settings saved", "success");
    onClose();
  };

  return (
    // qv-modal-overlay: register.css bottom-anchors this on phones, like every other register dialog.
    <div style={s.overlay} className="qv-modal-overlay" onClick={onClose}>
      <div style={s.card} className="qz-printer-settings" onClick={(e) => e.stopPropagation()}>
        {/* Neutralize the app's aggressive global `select {}` rules (margin-top:-1rem + !important
            blue border/padding) that would pull every dropdown up over its label and overwrite our
            theme styling. Scoped so nothing else in the app is affected. */}
        <style>{`
          .qz-printer-settings select {
            margin-top: 0 !important;
            height: 42px !important;
            padding: 0 12px !important;
            border: 1px solid ${t.inputBorder || "#25324d"} !important;
            border-radius: 9px !important;
            background-color: ${t.inputBg || "#0f1b30"} !important;
            color: ${t.inputText || "#E5EDF7"} !important;
            font-size: 14px !important;
          }
          .qz-printer-settings select option { margin-top: 0 !important; box-shadow: none !important; color: var(--ink); background: #fff; }
        `}</style>
        <div style={s.head}>
          <span>Printer Settings</span>
          <button style={s.x} onClick={onClose}>✕</button>
        </div>

        <div style={s.body}>
          <div style={s.detectRow}>
            <button style={s.detectBtn} onClick={detect} disabled={detecting}>{detecting ? "Detecting…" : "↻ Detect printers"}</button>
            {detectMsg && <span style={s.detectMsg}>{detectMsg}</span>}
          </div>

          <RoleEditor
            t={t}
            title="Payment Receipt Printer"
            cfg={receipt}
            setCfg={setReceipt}
            detected={detected}
            known={known}
            footNote="🔓 The cash drawer opens on this printer."
            actions={[
              { label: "Open drawer", busyKey: "Open drawer", fn: openDrawerOn },
              { label: "Test print", busyKey: "Receipt test print", fn: testPrintOn },
            ]}
            busy={busy}
            runTest={runTest}
          />

          <div style={s.role}>
            <div style={s.roleTitle}>Receipt paper width</div>
            <select style={s.input} value={paperWidth} onChange={(e) => setPaperWidth(Number(e.target.value))}>
              <option value={72}>80mm paper (72mm print)</option>
              <option value={48}>58mm paper (48mm print)</option>
            </select>
          </div>

          <RoleEditor
            t={t}
            title="Packing Slip Printer"
            cfg={packing}
            setCfg={setPacking}
            detected={detected}
            known={known}
            actions={[{ label: "Test print", busyKey: "Packing test print", fn: testPrintOn }]}
            busy={busy}
            runTest={runTest}
          />

          <div style={s.setup}>
            <div style={s.setupTitle}>First-time setup on this PC</div>
            <ol style={s.steps}>
              <li>
                Install <a style={s.link} href="https://qz.io/download/" target="_blank" rel="noreferrer">QZ Tray</a> and keep it running
                (it lets the browser reach the printer &amp; cash drawer).
              </li>
              <li>
                Install the{" "}
                <a style={s.link} href="https://starmicronics.com/support/products/tsp100iii-support-page/" target="_blank" rel="noreferrer">
                  Star TSP100 driver
                </a>
                .
              </li>
              <li>
                Add the printer to Windows by its IP — Star's <i>LAN &amp; Bluetooth Setup Tool</i>, or Settings → Printers &amp; scanners →
                Add manually → <i>Add by TCP/IP address</i>.
              </li>
              <li>
                Back here → <b>Detect</b> → pick the printer <b>by name</b> → <b>Test print</b>. The drawer opens via that same printer.
              </li>
            </ol>
            <div style={s.note}>
              A network printer not installed in Windows won't appear in Detect — install it (above) so it shows by name, or choose{" "}
              <b>Network (IP)</b> and enter its IP (drawer works, but receipt printing needs the installed driver).
            </div>
          </div>
        </div>

        <div style={s.footer}>
          <button style={s.cancel} onClick={onClose}>Cancel</button>
          <button style={s.save} onClick={save}>Save</button>
        </div>
      </div>
    </div>
  );
}

const NET = "__network__";
const netVal = (host, port) => `net:${host}:${Number(port) || 9100}`;

function RoleEditor({ t, title, cfg, setCfg, detected, known = [], footNote, actions, busy, runTest }) {
  const s = styles(t);
  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));
  const isNet = cfg.mode === "network";

  // One dropdown = detected (installed) printers + remembered network printers + "add network IP".
  const installed = cfg.name && !isNet && !detected.includes(cfg.name) ? [cfg.name, ...detected] : detected;
  const current = isNet && cfg.host ? `${cfg.host}:${Number(cfg.port) || 9100}` : "";
  const nets = current && !known.includes(current) ? [current, ...known] : known;

  const selectValue = isNet ? (cfg.host ? netVal(cfg.host, cfg.port) : NET) : cfg.name || "";
  const onSelect = (v) => {
    if (v === NET) return set({ mode: "network", host: "", port: 9100 });
    if (v.indexOf("net:") === 0) {
      const rest = v.slice(4);
      const i = rest.lastIndexOf(":");
      return set({ mode: "network", host: rest.slice(0, i), port: Number(rest.slice(i + 1)) || 9100 });
    }
    return set({ mode: "installed", name: v });
  };

  return (
    <div style={s.role}>
      <div style={s.roleTitle}>{title}</div>

      <select style={s.input} value={selectValue} onChange={(e) => onSelect(e.target.value)}>
        <option value="">— select a printer —</option>
        {installed.map((p) => (
          <option key={p} value={p}>{p}</option>
        ))}
        {nets.map((e) => (
          <option key={"net:" + e} value={"net:" + e}>🌐 {e.split(":")[0]} (network)</option>
        ))}
        <option value={NET}>＋ Add network printer (IP)…</option>
      </select>

      {isNet && (
        <div style={s.ipRow}>
          <input
            style={{ ...s.input, flex: 1 }}
            placeholder="Printer IP (e.g. 192.168.1.118)"
            value={cfg.host}
            onChange={(e) => set({ host: e.target.value })}
            inputMode="decimal"
            autoFocus
          />
          <input
            style={{ ...s.input, width: 90 }}
            placeholder="Port"
            value={cfg.port}
            onChange={(e) => set({ port: e.target.value.replace(/[^0-9]/g, "") })}
            inputMode="numeric"
          />
        </div>
      )}

      {footNote && <div style={s.footNote}>{footNote}</div>}

      <div style={s.actions}>
        {actions.map((a) => (
          <button key={a.label} style={s.testBtn} disabled={!!busy} onClick={() => runTest(a.busyKey, a.fn, cfg)}>
            {busy === a.busyKey ? "…" : a.label}
          </button>
        ))}
      </div>
    </div>
  );
}

const styles = (t) => ({
  overlay: { position: "fixed", inset: 0, background: "rgba(2,6,23,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: 12 },
  // Phones get sheet geometry (full width, bottom-anchored, 92dvh) from register.css — the rules are
  // `!important` because they have to beat these inline styles, which is also why the vh value here
  // stays as the fallback for anything that does not understand dvh.
  card: { width: "100%", maxWidth: 480, background: t.card || "#0f1a2f", color: t.text || "#E5EDF7", borderRadius: 16, border: `1px solid ${t.cardBorder || "#1f2c47"}`, overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: "92vh" },
  head: { flexShrink: 0, padding: "14px 18px", fontWeight: 800, fontSize: 16, borderBottom: `1px solid ${t.divider || "#1f2c47"}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, background: t.surfaceAlt || "#0f1a2f" },
  x: { border: "none", background: "none", cursor: "pointer", fontSize: 18, color: t.textMuted || "var(--ink-3)", minWidth: 44, minHeight: 44, borderRadius: 10, flex: "0 0 auto" },
  body: { flex: "1 1 auto", minHeight: 0, padding: 16, display: "flex", flexDirection: "column", gap: 14, overflowY: "auto", overflowX: "hidden" },
  // ⚠️ WRAPS, and the button never shrinks. The status message is free-form server text ("Detect
  // failed: Unable to establish connection with QZ Tray") and on a phone flex handed it most of the
  // row, squeezing the button until "Detect printers" broke onto two lines and spilled out of its
  // own 38px box. The button keeps its intrinsic width; the message takes the next line if it must.
  detectRow: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10 },
  detectBtn: { minHeight: 44, padding: "0 14px", borderRadius: 9, border: `1px solid ${t.cardBorder || "#25324d"}`, background: t.surfaceAlt || "#0f1b30", color: t.text || "#E5EDF7", fontWeight: 500, cursor: "pointer", whiteSpace: "nowrap", flex: "0 0 auto" },
  detectMsg: { fontSize: 12, color: t.textMuted || "var(--ink-3)", flex: "1 1 160px", minWidth: 0, overflowWrap: "anywhere" },
  role: { border: `1px solid ${t.cardBorder || "#25324d"}`, borderRadius: 12, padding: 12, display: "flex", flexDirection: "column", gap: 8 },
  roleTitle: { fontWeight: 500, fontSize: 14 },
  modeRow: { display: "flex", gap: 16 },
  radio: { display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: t.textMuted || "var(--ink-3)", cursor: "pointer" },
  input: { height: 42, borderRadius: 9, border: `1px solid ${t.inputBorder || "#25324d"}`, background: t.inputBg || "#0f1b30", color: t.inputText || "#E5EDF7", padding: "0 12px", fontSize: 14, outline: "none", boxSizing: "border-box", width: "100%" },
  ipRow: { display: "flex", flexWrap: "wrap", gap: 8 },
  footNote: { fontSize: 12, color: t.accent || "#4C8DFF" },
  actions: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 2 },
  // "Open drawer" physically opens a cash drawer — it does not get a 34px target.
  testBtn: { minHeight: 44, padding: "0 14px", borderRadius: 10, border: `1px solid ${t.cardBorder || "#25324d"}`, background: "transparent", color: t.text || "#E5EDF7", fontWeight: 500, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap", flex: "0 1 auto" },
  note: { fontSize: 12, color: t.textMuted || "var(--ink-3)", lineHeight: 1.5, marginTop: 8 },
  setup: { border: `1px solid ${t.cardBorder || "#25324d"}`, borderRadius: 12, padding: "10px 12px", background: t.surfaceAlt || "#0f1b30" },
  setupTitle: { fontWeight: 500, fontSize: 13, marginBottom: 4 },
  steps: { margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.6, color: t.text || "#E5EDF7", display: "flex", flexDirection: "column", gap: 4 },
  link: { color: t.accent || "#4C8DFF", fontWeight: 500, textDecoration: "underline" },
  // Bottom-anchored on a phone, so the footer sits over the home indicator without the inset.
  footer: { flexShrink: 0, padding: 14, paddingBottom: "calc(14px + env(safe-area-inset-bottom))", borderTop: `1px solid ${t.divider || "#1f2c47"}`, display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" },
  cancel: { height: 44, padding: "0 18px", borderRadius: 10, border: `1px solid ${t.cardBorder || "#25324d"}`, background: "transparent", color: t.text || "#E5EDF7", fontWeight: 500, cursor: "pointer" },
  save: { height: 44, padding: "0 26px", borderRadius: 10, border: "none", background: "#059669", color: "#fff", fontWeight: 800, cursor: "pointer" },
});
